import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SearchIcon } from '@synergy/theme';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import debounce from 'lodash.debounce';

import { PoolTextField } from '../../textfield';
import { useSearch } from '../context/search-context';

/** What the component used to carry, for themes without the contract. */
const LEGACY_SEARCH = {
    borderRadius: '24px',
    minWidth: '230px',
    paddingLeft: 18,
    paddingBlock: 9,
    fontSize: 13,
};

const useStyles = makeStyles((theme: Theme) => ({
    searchField: {
        '& > div': {
            borderRadius: (theme.searchField ?? LEGACY_SEARCH).borderRadius,
            minWidth: (theme.searchField ?? LEGACY_SEARCH).minWidth,
        },

        '& input': {
            paddingLeft: (theme.searchField ?? LEGACY_SEARCH).paddingLeft,
            paddingTop: (theme.searchField ?? LEGACY_SEARCH).paddingBlock,
            paddingBottom: (theme.searchField ?? LEGACY_SEARCH).paddingBlock,
            fontSize: (theme.searchField ?? LEGACY_SEARCH).fontSize,
        },
        '& input::placeholder': {
            color: theme.palette.grey['900'],
            opacity: 1,
        },

        '& input:focus::placeholder, & input:focus + span': {
            opacity: 0.6,
        },
    },
}));

export const SearchFilter = () => {
    const classes = useStyles();
    const { search, onUpdateSearch } = useSearch();
    const [value, setValue] = useState(search);
    const debouncedSearch = useMemo(() => debounce(onUpdateSearch, 300), [onUpdateSearch]);

    // We clear the input field if search param was removed/cleared from an external source
    useEffect(() => {
        if (!search) {
            setValue(search);
        }
    }, [search]);

    const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        debouncedSearch(e.target.value);
    };

    return (
        <FormattedMessage id="app.common.searchPlaceholder">
            {(text: string[]) => (
                <PoolTextField
                    className={classes.searchField}
                    placeholder={text[0]}
                    value={value}
                    onChange={handleValueChange}
                    endAdornment={<SearchIcon />}
                />
            )}
        </FormattedMessage>
    );
};
